"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Search, TrendingUp, MapPin, Target, ArrowRight, CircleCheck as CheckCircle2, Compass, Building2, Sparkles, Globe } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
const searchTrends = [{
  month: "Jan",
  queries: 3200
}, {
  month: "Feb",
  queries: 3450
}, {
  month: "Mar",
  queries: 3900
}, {
  month: "Apr",
  queries: 4100
}, {
  month: "May",
  queries: 4600
}, {
  month: "Jun",
  queries: 5200
}];
const keywordsData = [{
  keyword: "SEO consultant Hull",
  volume: "720/mo",
  difficulty: "Medium",
  intent: "Commercial / Direct Hire",
  opportunity: "High conversion intent for direct expert strategy"
}, {
  keyword: "Local SEO services Hull",
  volume: "890/mo",
  difficulty: "Low-Medium",
  intent: "Transactional",
  opportunity: "Captures local business decision-makers actively searching"
}, {
  keyword: "Digital marketing Hull Humber",
  volume: "1,400/mo",
  difficulty: "Medium",
  intent: "Commercial",
  opportunity: "Cross-regional B2B lead generation potential"
}, {
  keyword: "SEO audit Hull businesses",
  volume: "480/mo",
  difficulty: "Low",
  intent: "Transactional",
  opportunity: "Immediate technical overhaul and quick wins demand"
}];
const competitorGaps = [{
  title: "Outdated Directory Reliance",
  description: "Legacy Hull competitors rely heavily on obsolete regional directory links rather than modern entity-based local schema and structured local data.",
  impact: "High Opportunity for Modern Entity Schema"
}, {
  title: "Weak Localized Content Depth",
  description: "Agencies often publish thin templates lacking authentic Hull business context, local landmarks, or Humber regional industry alignment.",
  impact: "Fast Ranking Gains via Hyper-Local Content Silos"
}, {
  title: "Incomplete Google Business Profiles",
  description: "Inconsistent NAP data, missing explicit service areas, and unoptimized geotagged visual assets across local business listings.",
  impact: "Direct Map Pack Top 3 Rankings Capture"
}];
const neighboringCities = [{
  cityName: "Wakefield",
  description: "West Yorkshire commercial center with growing manufacturing and B2B services demand.",
  path: "/LocalSeoConsultantWakefieldPage",
  distance: "58 miles"
}, {
  cityName: "Derby",
  description: "East Midlands industrial hub focused on engineering and technology search growth.",
  path: "/LocalSeoConsultantDerbyPage",
  distance: "84 miles"
}, {
  cityName: "Stoke-on-Trent",
  description: "Staffordshire regional business center seeking modern digital expansion.",
  path: "/LocalSeoConsultantStokeOnTrentPage",
  distance: "110 miles"
}, {
  cityName: "Northampton",
  description: "Logistics and professional services powerhouse in the East Midlands.",
  path: "/LocalSeoConsultantNorthamptonPage",
  distance: "125 miles"
}, {
  cityName: "Milton Keynes",
  description: "Rapidly growing tech and enterprise hub with competitive local search terms.",
  path: "/LocalSeoConsultantMiltonKeynesPage",
  distance: "140 miles"
}];
export default function Hulllocalsearchanalysis() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="hulllocalsearchanalysis" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors duration-300 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Heading */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-100px"
      }} variants={containerVariants} className="flex flex-col items-center text-center max-w-3xl mx-auto mb-16">
          <motion.div variants={itemVariants}>
            <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-primary" />
              Regional Search Intelligence
            </Badge>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif text-foreground tracking-tight mb-6">
            Hull Local Search Landscape &amp; Strategic Opportunities
          </motion.h2>

          <motion.p variants={itemVariants} className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            When analyzing search patterns across Kingston upon Hull and the Humber region, I identify high-intent local queries, competitor weaknesses, and direct revenue opportunities. You work directly with me to capture local market share without agency bloat.
          </motion.p>
        </motion.div>

        {/* Top Insights Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-16 items-stretch">
          {/* Trends Visual Card */}
          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-7 flex flex-col">
            <Card className="h-full border-border bg-card shadow-sm flex flex-col justify-between">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <Badge variant="outline" className="border-primary/20 text-primary">
                    Hull &amp; Humber Market Demand
                  </Badge>
                  <TrendingUp className="w-5 h-5 text-primary" />
                </div>
                <CardTitle className="text-xl font-bold font-serif text-card-foreground mt-2">
                  Local Search Volume Growth
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  Consistent YoY surge in Hull commercial and service-related search queries.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6 pt-2">
                {/* Visual Bar Chart */}
                <div className="bg-muted/50 p-6 rounded-lg border border-border">
                  <div className="flex justify-between items-baseline mb-4">
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Monthly Query Trajectory
                    </span>
                    <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                      +62.5% Growth
                    </span>
                  </div>

                  <div className="grid grid-cols-6 gap-2 sm:gap-4 items-end h-36 pt-4">
                    {searchTrends.map((item, i) => {
                    const maxHeight = 5200;
                    const heightPercent = Math.round(item.queries / maxHeight * 100);
                    return <div data-index={i} key={item.month} className="flex flex-col items-center gap-2 group">
                          <span className="text-[11.5px] sm:text-xs font-mono font-medium text-muted-foreground group-hover:text-primary transition-colors">
                            {item.queries}
                          </span>
                          <div className="w-full bg-border rounded-t-sm h-24 flex items-end overflow-hidden">
                            <div className="w-full bg-primary/80 group-hover:bg-primary transition-all duration-300 rounded-t-sm" style={{
                          height: `${heightPercent}%`
                        }} />
                          </div>
                          <span className="text-xs font-medium text-foreground">{item.month}</span>
                        </div>;
                  })}
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-2">
                  <div className="bg-background p-4 rounded-lg border border-border text-center">
                    <span className="block text-2xl font-bold font-mono text-primary">68%</span>
                    <span className="text-xs text-muted-foreground">Mobile Local Intent</span>
                  </div>
                  <div className="bg-background p-4 rounded-lg border border-border text-center">
                    <span className="block text-2xl font-bold font-mono text-primary">3.4x</span>
                    <span className="text-xs text-muted-foreground">Map Pack Click Lead Rate</span>
                  </div>
                  <div className="bg-background p-4 rounded-lg border border-border text-center col-span-2 sm:col-span-1">
                    <span className="block text-2xl font-bold font-mono text-primary">Top 3</span>
                    <span className="text-xs text-muted-foreground">Google Map Target</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Strategic Value Card */}
          <motion.div initial={{
          opacity: 0,
          x: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-5 flex flex-col justify-between space-y-6">
            <Card className="border-border bg-card shadow-sm h-full flex flex-col justify-between">
              <CardHeader>
                <div className="w-10 h-10 rounded-lg bg-secondary/80 flex items-center justify-center text-secondary-foreground mb-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                </div>
                <CardTitle className="text-xl font-bold font-serif text-card-foreground">
                  Why Work Directly With Me for Hull SEO?
                </CardTitle>
                <CardDescription className="text-muted-foreground">
                  I bring senior hands-on SEO strategy tailored specifically to Hull businesses without delegating to junior staff.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-foreground">Direct Consultant Access</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      You communicate directly with me, Yassine Essaadi, for strategy, audits, and execution.
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Geotagged &amp; Entity Local Schema</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Advanced structured data targeting Hull and East Riding commercial entity relationships.
                    </p>
                  </div>
                </div>

                <Separator />

                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">Transparent ROI Measurement</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      No vanity metrics. I focus strictly on organic lead generation, revenue, and localized search dominance.
                    </p>
                  </div>
                </div>

                <div className="pt-4">
                  <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm">
                    <Link to="/ContactPage">
                      Book a Free Hull Consultation
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* High-Intent Keyword Opportunities */}
        <div className="mb-16">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
            <div>
              <Badge variant="outline" className="mb-2 border-primary/20 text-primary">
                High-Intent Keywords
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground">
                Hull Local Search Opportunities
              </h3>
            </div>
            <p className="text-muted-foreground text-sm max-w-md">
              A sample analysis of valuable commercial queries in Hull that produce rapid lead flow when properly targeted.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {keywordsData.map((item, i) => <motion.div data-index={i} key={item.keyword} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 15
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }}>
                <Card className="border-border bg-card hover:border-primary/40 transition-colors shadow-xs h-full flex flex-col justify-between">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <Search className="w-4 h-4 text-primary shrink-0" />
                        <span className="font-semibold text-base text-foreground font-mono">
                          {item.keyword}
                        </span>
                      </div>
                      <Badge variant="secondary" className="text-[12.5px] font-mono shrink-0">
                        {item.volume}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs mb-3 text-muted-foreground">
                      <div>
                        <span className="block font-medium text-foreground/80">Difficulty:</span>
                        <span>{item.difficulty}</span>
                      </div>
                      <div>
                        <span className="block font-medium text-foreground/80">Search Intent:</span>
                        <span>{item.intent}</span>
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground bg-muted/60 p-2.5 rounded-md border border-border/50">
                      <strong className="text-foreground font-medium">Strategic Value:</strong> {item.opportunity}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>)}
          </div>
        </div>

        {/* Competitor Gaps */}
        <div className="mb-16">
          <Card className="border-border bg-card shadow-sm overflow-hidden">
            <CardHeader className="bg-muted/30 border-b border-border">
              <div className="flex items-center gap-2 text-primary mb-1">
                <Target className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase tracking-wider">Competitive Landscape</span>
              </div>
              <CardTitle className="text-2xl font-bold font-serif text-foreground">
                Hull Competitor SEO Gaps I Exploit
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                Most agencies in the region rely on outdated practices. Here is how my tailored strategy outperforms them:
              </CardDescription>
            </CardHeader>
            <CardContent className="p-6 md:p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {competitorGaps.map((gap, i) => <div data-index={i} key={gap.title} className="flex flex-col justify-between bg-background p-5 rounded-lg border border-border">
                    <div>
                      <div className="w-8 h-8 rounded-md bg-primary/10 text-primary flex items-center justify-center font-bold font-mono text-sm mb-3">
                        0{i + 1}
                      </div>
                      <h4 className="font-semibold text-foreground mb-2 text-base">{gap.title}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                        {gap.description}
                      </p>
                    </div>
                    <div className="mt-auto pt-3 border-t border-border">
                      <span className="text-[12.5px] font-semibold text-primary block">
                        {gap.impact}
                      </span>
                    </div>
                  </div>)}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Neighboring Regional Hubs Network */}
        <div className="mb-16">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs uppercase tracking-wider">
              <Compass className="w-3.5 h-3.5 mr-1 text-primary" />
              Regional Connectivity
            </Badge>
            <h3 className="text-2xl sm:text-3xl font-bold font-serif text-foreground mb-3">
              Targeting Neighboring Cities &amp; Commercial Hubs?
            </h3>
            <p className="text-muted-foreground text-sm">
              I deliver tailored local SEO strategy across major regional commercial hubs in Yorkshire, the East Midlands, and beyond.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {neighboringCities.map((city, i) => <motion.div data-index={i} key={city.cityName} whileHover={{
            y: -3
          }} transition={{
            duration: 0.2
          }}>
                <Card className="border-border bg-card hover:border-primary/50 transition-colors shadow-xs h-full flex flex-col justify-between">
                  <CardContent className="p-5 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-primary" />
                          <h4 className="font-bold text-foreground text-base">{city.cityName}</h4>
                        </div>
                        <span className="text-[12.5px] font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded">
                          {city.distance}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                        {city.description}
                      </p>
                    </div>

                    <Button variant="ghost" size="sm" asChild className="w-full justify-between text-primary hover:text-primary hover:bg-primary/5 p-0 h-auto font-medium text-xs">
                      <Link to={city.path}>
                        <span>Explore {city.cityName} SEO</span>
                        <ArrowRight className="w-3.5 h-3.5 ml-1" />
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>)}

            {/* National / Multi-location Card */}
            <motion.div whileHover={{
            y: -3
          }} transition={{
            duration: 0.2
          }}>
              <Card className="border-primary/30 bg-primary/5 shadow-xs h-full flex flex-col justify-between">
                <CardContent className="p-5 flex flex-col justify-between h-full">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Globe className="w-4 h-4 text-primary" />
                      <h4 className="font-bold text-foreground text-base">National &amp; Multi-Location</h4>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-4">
                      Expanding beyond local boundaries? Scalable SEO frameworks for national campaigns and multiple UK locations.
                    </p>
                  </div>

                  <Button variant="ghost" size="sm" asChild className="w-full justify-between text-primary hover:text-primary hover:bg-primary/10 p-0 h-auto font-medium text-xs">
                    <Link to="/NationalSeoPage">
                      <span>View National SEO Services</span>
                      <ArrowRight className="w-3.5 h-3.5 ml-1" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>

        {/* Direct CTA */}
        <motion.div initial={{
        opacity: 0,
        y: shouldReduceMotion ? 0 : 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-card border border-border rounded-xl p-8 md:p-12 text-center shadow-sm relative overflow-hidden">
          <div className="relative z-10 max-w-2xl mx-auto space-y-6">
            <Badge variant="secondary" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold">
              Direct Expert Partnership
            </Badge>

            <h3 className="text-xl sm:text-2xl font-bold font-serif text-foreground">
              Ready to Capture Hull Search Traffic?
            </h3>

            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              Skip agency middlemen. Speak directly with me, Yassine Essaadi, to audit your current search presence and craft a tailored local SEO strategy built for sustainable growth.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 px-8">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>

              <Button variant="outline" size="lg" asChild className="w-full sm:w-auto border-border hover:bg-muted">
                <Link to="/LocalSEOPage">
                  Explore All Local SEO Services
                </Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}